<script setup lang="ts">

import { Direction } from '../fin/optimisation';
import MontecarloRetirementOutput from './MontecarloRetirementOutput.vue';
import { ref, Ref } from 'vue';
import { RetirementConfig } from '../fin/retirement_config';
import RetirementConfigElem from './RetirementConfigElem.vue';
import { RetirementState } from '../fin/retirement_state';
import { MultipleHistory } from '../fin/retirement';
import { TimeSeriesS } from '../fin/history';
import { Quantiles } from '../fin/quantile';
import * as wc from '../worker/client.ts';
import * as ww from '../worker/worker.ts';

interface Loadable {
  load(): void;
}

const retirementConfigElemRef = ref<Loadable|null>(null);
const earlyPensionWithdrawalGuidance = ref<Direction>(Direction.NO_GUIDANCE);
const postRetirementIsaSavingGuidance = ref<Direction>(Direction.NO_GUIDANCE);

let assetTimeQuantilesSeries: Ref<TimeSeriesS<Quantiles>> = ref(new TimeSeriesS<Quantiles>([]));
let birthYear: Ref<number> = ref<number>(0);
const retirementYear: Ref<number> = ref<number>(0);
const maxYear: Ref<number> = ref<number>(0);
const completionPercentage: Ref<number> = ref<number>(0);
let currentCalculationTaskId = '';
const emit = defineEmits(['configChanged']);

function calculate(config: RetirementConfig): void {
  console.log('starting MontecarloRetirement.vue calculate()');
  retirementConfigElemRef.value?.load();

  const pre_retirement_rs: RetirementState = new RetirementState(
    config.person.simulation_start_year,
    config.person,
    config.initialAssets,
    config.market,
    [],
  );

  if (currentCalculationTaskId !== '') {
    wc.cancelTask(currentCalculationTaskId);
  }

  currentCalculationTaskId = wc.requestMonteCarloRetirement(
    config,
    {
      completionHandler: (
        function(r: ww.TaskResponseMessage) {
          console.log('got worker task completion message');
          currentCalculationTaskId = '';
          const atqs = MultipleHistory.deserialize(r.serializedResult)
          assetTimeQuantilesSeries.value = atqs.histories;
          birthYear.value = config.person.birth_date.getUTCFullYear();
          retirementYear.value = pre_retirement_rs.person.retirement_date.getUTCFullYear();
          maxYear.value = atqs.maxYear;
          completionPercentage.value = 100;
        }
      ),
      failureHandler: () => console.warn('got worker task failure message'),
      progressHandler: (progress: number) => {
        console.debug(`got worker task progress message: ${progress}`);
        completionPercentage.value = Math.round(progress * 100);
      }
    } as wc.Listener,
  );
}

defineExpose({calculate});

</script>

<template>
  <div activityName="MontecarloRetirement">
    <h2>MontecarloRetirement</h2>
    <div class="explainer-card">
      Explore multiple retirements using randomly varying market conditions
      according to configurable statistical properties, so as to explore the
      likelihood of running out of money early.
      <br/>
      This tool is the best way to investigate scheduling risk: The chance that
      an unlucky few years at the beginning could have disproportionate impact
      on the rest of your retirement.
    </div>
    <RetirementConfigElem
      ref="retirementConfigElemRef"
      @configChanged="(cfg) => {calculate(cfg); emit('configChanged', cfg);}"
      :showMarketUncertainty="true"
      :earlyPensionWithdrawalGuidance="earlyPensionWithdrawalGuidance"
      :postRetirementIsaSavingGuidance="postRetirementIsaSavingGuidance"
    />
    <div>
      <table>
        <tbody>
          <tr>
            <td>Calculation progress</td>
            <td>{{completionPercentage}}%</td>
          </tr>
        </tbody>
      </table>
    </div>
    <MontecarloRetirementOutput
      :assetTimeQuantilesSeriess="assetTimeQuantilesSeries"
      :birthYear="birthYear"
      :retirementYear="retirementYear"
      :maxYear="maxYear"
    />
  </div>
</template>

<style scoped>
</style>
